import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowUp, ArrowDown } from "lucide-react";
import GlobalFilter from "@/components/common/GlobalFilter";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { useAccount } from "@/contexts/account.context";
import { getChannelMonthlyAnalysisApiV1ChannelMonthlyAnalysisGet } from "@/backend-sdk/client/sdk.gen";
import type {
  ChannelAnalysisMonthlyRow,
  ChannelAnalysisTotals,
  ChannelMonthlyAnalysisChannel,
} from "@/backend-sdk/client/types.gen";
import {
  formatCurrency as formatCurrencyUtil,
  formatInteger,
  formatDecimal,
} from "@/utils/numberFormat";

type MonthData = ChannelAnalysisMonthlyRow;
type ChannelTotals = ChannelAnalysisTotals;
type ChannelData = ChannelMonthlyAnalysisChannel;

// ── Formatadores ───────────────────────────────────────────────────
const fmtCurrency = (v: number): string => formatCurrencyUtil(v);
const fmtPercent = (v: number | null | undefined): string =>
  v === null || v === undefined ? "—" : `${formatDecimal(v, 2)}%`;
const fmtNumber = (v: number): string => formatInteger(v);

// ── Definição de linhas (métricas) ─────────────────────────────────
interface MetricDef {
  label: string;
  getValue: (d: MonthData) => string;
  getTotal: (t: ChannelTotals) => string;
  requiresInvestment?: boolean;
  isGeneralOnly?: boolean;
}

const GENERAL_METRICS: MetricDef[] = [
  {
    label: "Receita",
    getValue: (d) => fmtCurrency(d.revenue),
    getTotal: (t) => fmtCurrency(t.revenue),
  },
  {
    label: "Taxa de Conversão",
    getValue: (d) => fmtPercent(d.conversion_rate),
    getTotal: (t) => fmtPercent(t.conversion_rate),
  },
  {
    label: "Pedidos",
    getValue: (d) => fmtNumber(d.orders),
    getTotal: (t) => fmtNumber(t.orders),
  },
  {
    label: "Sessões",
    getValue: (d) => fmtNumber(d.sessions),
    getTotal: (t) => fmtNumber(t.sessions),
  },
  {
    label: "Investimento",
    getValue: (d) => fmtCurrency(d.investment ?? 0),
    getTotal: (t) => fmtCurrency(t.investment ?? 0),
    requiresInvestment: true,
  },
  {
    label: "CPS",
    getValue: (d) => fmtCurrency(d.cps ?? 0),
    getTotal: (t) => fmtCurrency(t.cps ?? 0),
    requiresInvestment: true,
  },
  {
    label: "ROAS",
    getValue: (d) => fmtCurrency(d.roas ?? 0),
    getTotal: (t) => fmtCurrency(t.roas ?? 0),
    requiresInvestment: true,
  },
];

const CHANNEL_BASE_METRICS: MetricDef[] = [
  {
    label: "Receita",
    getValue: (d) => fmtCurrency(d.revenue),
    getTotal: (t) => fmtCurrency(t.revenue),
  },
  {
    label: "% da Receita Geral",
    getValue: (d) => fmtPercent(d.revenue_percent ?? null),
    getTotal: (t) => fmtPercent(t.revenue_percent),
  },
  {
    label: "Taxa de Conversão",
    getValue: (d) => fmtPercent(d.conversion_rate),
    getTotal: (t) => fmtPercent(t.conversion_rate),
  },
  {
    label: "Pedidos",
    getValue: (d) => fmtNumber(d.orders),
    getTotal: (t) => fmtNumber(t.orders),
  },
  {
    label: "Sessões",
    getValue: (d) => fmtNumber(d.sessions),
    getTotal: (t) => fmtNumber(t.sessions),
  },
  {
    label: "% Sessões do Total",
    getValue: (d) => fmtPercent(d.sessions_percent ?? null),
    getTotal: (t) => fmtPercent(t.sessions_percent),
  },
];

const CHANNEL_INVESTMENT_METRICS: MetricDef[] = [
  {
    label: "Investimento",
    getValue: (d) => fmtCurrency(d.investment ?? 0),
    getTotal: (t) => fmtCurrency(t.investment ?? 0),
    requiresInvestment: true,
  },
  {
    label: "CPS",
    getValue: (d) => fmtCurrency(d.cps ?? 0),
    getTotal: (t) => fmtCurrency(t.cps ?? 0),
    requiresInvestment: true,
  },
  {
    label: "ROAS",
    getValue: (d) => fmtCurrency(d.roas ?? 0),
    getTotal: (t) => fmtCurrency(t.roas ?? 0),
    requiresInvestment: true,
  },
];

// ── Componente de Tabela do Canal ──────────────────────────────────
const ChannelTable: React.FC<{ channel: ChannelData }> = ({ channel }) => {
  const [sortAsc, setSortAsc] = useState(false); // Meses mais recentes primeiro

  const isGeneral = channel.key === "geral_total";

  const hasData =
    channel.monthly_data.length > 0 &&
    channel.monthly_data.some(
      (d) => d.sessions > 0 || d.revenue > 0 || d.orders > 0,
    );

  const metrics = isGeneral
    ? GENERAL_METRICS
    : channel.has_investment
      ? [...CHANNEL_BASE_METRICS, ...CHANNEL_INVESTMENT_METRICS]
      : CHANNEL_BASE_METRICS;

  const sortedMonths = useMemo(() => {
    const months = [...channel.monthly_data];
    months.sort((a, b) =>
      sortAsc
        ? a.month_key.localeCompare(b.month_key)
        : b.month_key.localeCompare(a.month_key),
    );
    return months;
  }, [channel.monthly_data, sortAsc]);

  const toggleSort = () => setSortAsc((prev) => !prev);

  return (
    <Card className="hover:shadow-md transition-shadow h-fit overflow-visible">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2 w-full">
          <div className="flex-1 min-w-[150px]">
            <CardTitle>{channel.name}</CardTitle>
          </div>
          {channel.description && (
            <span className="text-xs text-muted-foreground flex-shrink-0">
              {channel.description}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs">
                <tr>
                  <th
                    className="pl-3 pr-2 py-2 text-right whitespace-nowrap sticky left-0 bg-muted z-10 min-w-[140px] cursor-pointer hover:bg-muted/80 transition-colors select-none"
                    onClick={toggleSort}
                  >
                    <span className="inline-flex items-center gap-1 justify-end w-full">
                      Ano e mês
                      {sortAsc ? (
                        <ArrowUp className="h-3 w-3 opacity-60" />
                      ) : (
                        <ArrowDown className="h-3 w-3 opacity-60" />
                      )}
                    </span>
                  </th>
                  {hasData ? (
                    <>
                      {sortedMonths.map((d) => (
                        <th
                          key={d.month_key}
                          className="px-2.5 py-2 text-right whitespace-nowrap min-w-[110px]"
                        >
                          {d.month_label}
                        </th>
                      ))}
                      <th className="px-2.5 py-2 text-right whitespace-nowrap min-w-[120px] font-bold">
                        TOTAL
                      </th>
                    </>
                  ) : (
                    <>
                      <th className="px-2.5 py-2 text-right whitespace-nowrap">
                        Não há dados
                      </th>
                      <th className="px-2.5 py-2 text-right whitespace-nowrap min-w-[120px] font-bold">
                        TOTAL
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="text-xs">
                {metrics.map((m, idx) => (
                  <tr
                    key={m.label}
                    className={`border-b border-border hover:bg-muted/50 ${
                      idx % 2 === 0 ? "bg-muted/20" : "bg-background"
                    }`}
                  >
                    <td className="pl-3 pr-2 py-2 text-right whitespace-nowrap font-semibold sticky left-0 z-10 bg-inherit min-w-[140px]">
                      {m.label}
                    </td>
                    {hasData ? (
                      <>
                        {sortedMonths.map((d) => (
                          <td
                            key={d.month_key}
                            className="px-2.5 py-2 text-right whitespace-nowrap"
                          >
                            {m.getValue(d)}
                          </td>
                        ))}
                        <td className="px-2.5 py-2 text-right whitespace-nowrap font-semibold">
                          {m.getTotal(channel.totals)}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-2.5 py-2 text-right whitespace-nowrap text-muted-foreground">
                          -
                        </td>
                        <td className="px-2.5 py-2 text-right whitespace-nowrap"></td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ── Página Principal ───────────────────────────────────────────────
const AnaliseMensalPage: React.FC = () => {
  const navigate = useNavigate();
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();

  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState<ChannelData[]>([]);

  const startDate = useMemo(
    () => formatDateToISO(filterState.dateRange.startDate),
    [filterState.dateRange.startDate],
  );
  const endDate = useMemo(
    () => formatDateToISO(filterState.dateRange.endDate),
    [filterState.dateRange.endDate],
  );

  useEffect(() => {
    const fetchData = async () => {
      const customerUuid =
        selectedAccount?.customer_uuid ||
        localStorage.getItem("active_customer_uuid") ||
        undefined;
      if (!customerUuid) return;

      setLoading(true);
      try {
        const { data } =
          await getChannelMonthlyAnalysisApiV1ChannelMonthlyAnalysisGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              customer_uuid: customerUuid,
            },
            throwOnError: true,
          });
        setChannels(data?.channels || []);
      } catch (err) {
        console.error("Erro ao buscar análise mensal por canal:", err);
        setChannels([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate, selectedAccount]);

  return (
    <div className="p-6 space-y-4 page-background">
      {/* Header com título e botões */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="page-title">ANÁLISE MENSAL POR CANAL</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-3 text-xs font-medium"
            onClick={() => navigate("/dashboard/analise-diaria")}
          >
            ANÁLISE DIÁRIA
          </Button>
        </div>
      </div>

      {/* Filtro global */}
      <GlobalFilter />

      {/* Tabelas por canal */}
      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="hover:shadow-md transition-shadow h-fit">
              <CardHeader>
                <Skeleton className="h-5 w-40" />
              </CardHeader>
              <CardContent className="p-0">
                <Skeleton className="h-[180px] w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : channels.length === 0 ? (
        <Card className="hover:shadow-md transition-shadow h-fit">
          <CardContent className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
            Nenhum dado disponível para o período selecionado
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {channels.map((ch) => (
            <ChannelTable key={ch.key} channel={ch} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AnaliseMensalPage;
